import { useState } from 'react'
import { Link } from 'react-router-dom'
import MarketingLayout from '../layouts/MarketingLayout.jsx'
import SEOHead from '../components/SEOHead.jsx'
import CTAButton from '../components/CTAButton.jsx'
import { FAQ_CATEGORIES } from '../data/faq.js'

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_CATEGORIES.flatMap(cat =>
    cat.questions.map(item => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    }))
  ),
}

function CategorySection({ cat }) {
  const [openIndexes, setOpenIndexes] = useState([])

  function toggle(i) {
    setOpenIndexes(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i])
  }

  return (
    <section id={cat.id} className="scroll-mt-24">
      <h2 className="font-display font-semibold text-2xl text-kart-night mb-6 pb-3 border-b-2 border-kart-blue/20">
        {cat.label}
      </h2>
      <div className="space-y-0 divide-y divide-slate-200">
        {cat.questions.map((item, i) => (
          <div key={i}>
            <button
              onClick={() => toggle(i)}
              className="w-full flex items-start justify-between gap-4 py-5 text-left group"
              aria-expanded={openIndexes.includes(i)}
            >
              <h3 className="font-display font-medium text-kart-night text-base leading-snug group-hover:text-kart-blue transition-colors">
                {item.q}
              </h3>
              <span
                className={`shrink-0 mt-0.5 w-6 h-6 rounded-full flex items-center justify-center border-2 transition-all duration-200 ${
                  openIndexes.includes(i)
                    ? 'border-kart-blue bg-kart-blue text-white rotate-45'
                    : 'border-slate-300 text-slate-400'
                }`}
                aria-hidden="true"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 5v14M5 12h14" />
                </svg>
              </span>
            </button>
            <div
              className={`overflow-hidden transition-all duration-300 ${openIndexes.includes(i) ? 'max-h-96 pb-5' : 'max-h-0'}`}
              aria-hidden={!openIndexes.includes(i)}
            >
              <p className="text-slate-600 leading-relaxed">{item.a}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

export default function FAQPage() {
  return (
    <MarketingLayout>
      <SEOHead
        title="FAQ KartQuiz : toutes les questions des profs"
        description="Comment ça marche, pour quels élèves, quel tarif, RGPD, comparaison avec Kahoot. Toutes les réponses."
        canonical="/faq"
        jsonLd={faqJsonLd}
      />

      {/* Hero */}
      <section className="bg-[#F8FAFC] py-20 md:py-28 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h1 className="font-display font-semibold text-4xl md:text-5xl text-kart-night mb-6">
            FAQ KartQuiz
          </h1>
          <p className="text-slate-600 text-xl">
            Toutes les questions des profs, des formateurs et des curieux — avec des réponses directes.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-16 md:py-24">
        <div className="flex flex-col lg:flex-row gap-12">

          {/* Sidebar navigation */}
          <aside className="lg:w-56 shrink-0">
            <div className="sticky top-24">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">Catégories</p>
              <nav className="space-y-1">
                {FAQ_CATEGORIES.map(cat => (
                  <a
                    key={cat.id}
                    href={`#${cat.id}`}
                    className="block px-3 py-2 rounded-lg text-sm text-slate-600 hover:text-kart-blue hover:bg-kart-blue/5 transition-colors"
                  >
                    {cat.label}
                  </a>
                ))}
              </nav>
              <div className="mt-8 p-4 bg-kart-action/10 rounded-xl">
                <p className="text-sm text-slate-700 font-medium mb-3">Une question sans réponse ?</p>
                <a
                  href="mailto:contact@kartquiz.fr"
                  className="text-sm text-kart-action font-medium hover:underline"
                >
                  contact@kartquiz.fr →
                </a>
              </div>
            </div>
          </aside>

          {/* Questions */}
          <div className="flex-1 space-y-16">
            {FAQ_CATEGORIES.map(cat => (
              <CategorySection key={cat.id} cat={cat} />
            ))}
          </div>
        </div>
      </div>

      {/* CTA final */}
      <section className="py-20 bg-kart-night text-white text-center">
        <div className="max-w-2xl mx-auto px-6 space-y-6">
          <h2 className="font-display font-semibold text-3xl">Convaincu ?</h2>
          <p className="text-slate-300 text-lg">Crée ton compte gratuit et lance ta première course aujourd'hui.</p>
          <CTAButton to="/login" size="lg">Créer un compte gratuit</CTAButton>
          <p className="text-slate-500 text-sm">
            Voir aussi : <Link to="/comparatif" className="underline hover:text-slate-400">le comparatif avec Kahoot et Quizizz →</Link>
          </p>
        </div>
      </section>
    </MarketingLayout>
  )
}
